// client/src/features/reports/search.ts — SCR-REPT-002's and SCR-REPT-003's URL state ·
// REPORT-003 (04 §9.2.3, §8.14).
//
// §9.2.3 is NORMATIVE: a filtered view lives in the URL „so a filtered ledger is shareable,
// survives a refresh, and browser Back is a filter-undo". Here it carries the acceptance line of
// 05 REPORT-003 as well — „exports carry the same filters as the preview" — because the export
// is built from the same resolved window the payload was fetched with, and both are read from
// this one schema.
//
// THE TRAP D-1197 NAMES APPLIES TO NUMBERS TOO. TanStack Router parses search values with
// `JSON.parse`, so `?year=2025` typed by a human arrives as the NUMBER 2025, while `?month=03`,
// which a person will also write, is not valid JSON and arrives as the STRING „03".
// `z.coerce.number()` admits both spellings and is what `features/fiscal/search.ts` already uses
// for `year`. The enum params are safe as they are: „YEAR" is not valid JSON either, so it
// reaches the schema as a string and `z.enum` reads it.
//
// NO ZOD `.default()` ON A YEAR, for the reason `features/fiscal/search.ts` gives and this
// screen makes sharper: a default evaluated at parse time bakes „the year the link was opened"
// into every URL the router normalises, so a report shared in December would mean a different
// window to whoever opens it in January. The window is resolved once, explicitly, by
// `resolveFilters` below.

import { z } from 'zod';

import type { PnlBasis, ReportCatalogEntry, ReportingPeriodKind } from '@/client';

/** The calendar the schema accepts — `ReportingPeriod.MinYear`/`MaxYear`, 03 §8.6's own CHECK. */
export const MIN_REPORT_YEAR = 2000;
export const MAX_REPORT_YEAR = 2100;

const PERIOD_KINDS = [
  'MONTH',
  'QUARTER',
  'YEAR',
  'YTD',
] as const satisfies readonly ReportingPeriodKind[];

const BASES = ['ACCRUAL', 'CASH'] as const satisfies readonly PnlBasis[];

export const REPORT_PERIOD_KINDS: readonly ReportingPeriodKind[] = PERIOD_KINDS;
export const REPORT_BASES: readonly PnlBasis[] = BASES;

/**
 * SCR-REPT-002 — one report, one window, one scope.
 *
 * The names are the API's own (`period`, `year`, `month`, `quarter`, `basis`, `propertyId`,
 * `ownerId`), so the search params ARE the query on both the payload route and the export
 * route. There is no second, client-side notion of „filtered" for the two to disagree about.
 */
export const reportSearchSchema = z.object({
  period: z.enum(PERIOD_KINDS).optional(),
  year: z.coerce.number().int().min(MIN_REPORT_YEAR).max(MAX_REPORT_YEAR).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  quarter: z.coerce.number().int().min(1).max(4).optional(),
  basis: z.enum(BASES).optional(),
  propertyId: z.string().uuid().optional(),
  ownerId: z.string().uuid().optional(),
});

export type ReportSearch = z.infer<typeof reportSearchSchema>;

/**
 * SCR-REPT-003 — the wizard remembers the year, so a chosen year is a link. There is no owner in
 * it (D-2410): the dossier is the session's, and an old link still carrying `ownerId` loses it to
 * zod's default strip rather than failing to parse.
 */
export const dossierSearchSchema = z.object({
  year: z.coerce.number().int().min(MIN_REPORT_YEAR).max(MAX_REPORT_YEAR).optional(),
});

export type DossierSearch = z.infer<typeof dossierSearchSchema>;

/** Every filter of the window and the scope, resolved — what the payload and the export are sent. */
export interface ReportFilters {
  period: ReportingPeriodKind;
  year: number;
  month: number | undefined;
  quarter: number | undefined;
  basis: PnlBasis;
  propertyId: string | undefined;
  ownerId: string | undefined;
}

/**
 * THE PERIOD DEFAULT, and the answer to „fiscal year or calendar year?" (D-1472).
 *
 * In Romania an individual's fiscal year IS the calendar year — 01 §18 has no other — so the
 * question is never „which calendar" but „WHICH calendar year", and the two halves of the
 * gallery answer it differently because they are asked different questions.
 *
 * An ECONOMIC or OPERATIONAL report answers „how is the portfolio doing?", which is a question
 * about now: the default is the CURRENT year, to date. That is also the server's own default on
 * every payload route (`period=YTD`, `year` = the current Romanian year), so an unfiltered
 * screen and an unfiltered API call show the same figures.
 *
 * A FISCAL report answers „what am I filing, and for what?", which is a question about the year
 * that ENDED: a D212 for 2025 income is filed in 2026 (01 §18). The default is therefore the
 * PREVIOUS year — the same default `resolveFiscalYear` gives SCR-FISC-001, so the obligations
 * screen and the obligations report open on the same year rather than one year apart.
 *
 * This is also the one place the client's default differs from the server's: the two fiscal
 * payload routes default `fiscalYear` to the CURRENT calendar year. The screen therefore always
 * sends the year explicitly rather than relying on the omission, which is what makes the shared
 * link stable as well.
 */
export function defaultYear(entry: ReportCatalogEntry, now: Date): number {
  return entry.family === 'FISCAL' ? now.getFullYear() - 1 : now.getFullYear();
}

/**
 * The URL's filters, resolved against the report the URL is about.
 *
 * A filter the report does not take is DROPPED here rather than sent: the server answers a
 * property on the portfolio P&L with 422 `report-filter-not-supported` (D-1449), and a stale
 * `?propertyId=` carried over from the previous report in the gallery is not a reason to show
 * the user a refusal. What is dropped is visible — the panel does not offer the control — so
 * nothing is silently narrowed, which is the failure the server's own rule exists to prevent.
 */
export function resolveFilters(
  entry: ReportCatalogEntry,
  search: ReportSearch,
  now: Date,
): ReportFilters {
  const period = search.period ?? 'YTD';

  return {
    period,
    year: search.year ?? defaultYear(entry, now),
    month: period === 'MONTH' ? (search.month ?? now.getMonth() + 1) : undefined,
    quarter: period === 'QUARTER' ? (search.quarter ?? quarterOf(now)) : undefined,
    basis: entry.supportsBasis ? (search.basis ?? 'ACCRUAL') : 'ACCRUAL',
    propertyId: entry.supportsProperty ? search.propertyId : undefined,
    ownerId: entry.supportsOwner ? search.ownerId : undefined,
  };
}

/**
 * Whether the screen can ask for this report at all.
 *
 * The one report with `requiresProperty` is the per-property P&L, and a request without a
 * property is 422 `report-filter-not-supported` on both the payload route and the export. The
 * screen neither sends it nor guesses a property — see `ReportViewer`, and D-1471.
 */
export function isAskable(entry: ReportCatalogEntry, filters: ReportFilters): boolean {
  return !entry.requiresProperty || filters.propertyId !== undefined;
}

/** 1..4 for a date, the way `ReportingPeriod.Resolve` computes it. */
export function quarterOf(now: Date): number {
  return Math.floor(now.getMonth() / 3) + 1;
}

/**
 * The years a report may be asked for, newest first — the `<select>`'s options.
 *
 * Bounded above by the current year, because a report of a year that has not started is a page
 * of zeros and a YTD of one is refused outright (`reporting-period-in-future`), and below by
 * ten years or `MIN_REPORT_YEAR`, whichever is later.
 */
const YEAR_CHOICES = 10;

export function yearChoices(now: Date): number[] {
  const newest = now.getFullYear();
  const oldest = Math.max(MIN_REPORT_YEAR, newest - YEAR_CHOICES + 1);
  const years: number[] = [];
  for (let year = newest; year >= oldest; year -= 1) years.push(year);
  return years;
}
